import {
  generateChartingContributions,
  generateChartingSignature,
} from "@/lib/signatures";
import { Encounter } from "@curai/js-fhir-tools";
import type * as fhirTypes from "fhir/r4";
import { useMemo } from "react";

export const useSignatures = (encounter: fhirTypes.Encounter) => {
  const encounterModel = new Encounter(encounter);
  const {
    admittingParticipant,
    primaryParticipant,
    secondaryParticipants,
    isAsyncVisit,
    periodEnd,
  } = encounterModel;

  const admittingSignature = useMemo(() => {
    if (isAsyncVisit) return; // async visits only have primary signature
    if (
      admittingParticipant?.individual &&
      admittingParticipant?.period?.start
    ) {
      return generateChartingSignature({
        practitioner: admittingParticipant.individual,
        participantType: Encounter.ParticipantType.ADMITTING,
        timestamp: admittingParticipant.period?.start,
      });
    }
  }, [isAsyncVisit, admittingParticipant]);

  const primarySignature = useMemo(() => {
    if (primaryParticipant?.individual && periodEnd) {
      return generateChartingSignature({
        practitioner: primaryParticipant.individual,
        participantType: Encounter.ParticipantType.PRIMARY,
        timestamp: periodEnd,
      });
    }
  }, [periodEnd, primaryParticipant]);

  const secondariesSignature = useMemo(() => {
    if (isAsyncVisit) return; // async visits only have primary signature

    const secondaryPractitioners = secondaryParticipants
      .filter(
        ({ individual }) =>
          !!individual &&
          individual.identifier?.value !==
            admittingParticipant?.individual?.identifier?.value &&
          individual.identifier?.value !==
            primaryParticipant?.individual?.identifier?.value,
      )
      .map(({ individual }) => individual) as fhirTypes.Reference[]; // casting to prevent (fhirTypes.Reference | undefined[]) as we explicitly check for the existance of `.individual` above

    if (secondaryPractitioners.length) {
      return generateChartingContributions(secondaryPractitioners);
    }
  }, [
    isAsyncVisit,
    secondaryParticipants,
    admittingParticipant,
    primaryParticipant,
  ]);

  return { admittingSignature, primarySignature, secondariesSignature };
};
